/**
 * AuthContext - Global Authentication State
 *
 * ONLY talks to backend API - NEVER to Supabase directly
 * Uses JWT tokens stored in localStorage
 *
 * Supports two session types:
 * - User session (profile exists) → auth_token + user_data
 * - Applicant session (email verified, no profile) → applicant_token + applicant_data
 *
 * Usage:
 * const { user, applicant, login, logout } = useAuth()
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { User, ApplicantUser } from './types'
import { clearAccessLevelCache } from '@/hooks/useAccessLevel'

// Extend Window interface for global logout handler
declare global {
  interface Window {
    __authLogout?: () => void
  }
}

// Context shape - what components can access
interface AuthContextType {
  user: User | null
  applicant: ApplicantUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isApplicant: boolean
  login: (email: string, code: string) => Promise<{ needsWelcome?: boolean; isApplicant?: boolean }>
  loginWithGoogle: (idToken: string) => Promise<{ needsWelcome?: boolean; isApplicant?: boolean }>
  logout: () => Promise<void>
  logoutApplicant: () => void
  requestOTP: (email: string) => Promise<{ isApplicant?: boolean }>
  error: string | null
  clearError: () => void
}

// Create context
const AuthContext = createContext<AuthContextType | null>(null)

// Provider component - wraps the entire app
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [applicant, setApplicant] = useState<ApplicantUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check for existing session on mount
  useEffect(() => {
    checkSession()
  }, [])

  // Global logout - called when 401 detected or manual logout
  const performLogout = useCallback(() => {
    // Drop the presence ring in the admin dashboard straight away rather than
    // letting it linger for the 60s TTL. Must happen BEFORE the token is
    // cleared, since the endpoint is authenticated. Entirely fire-and-forget:
    // if this is a 401-triggered logout the call simply fails, which is fine -
    // presence is decoration and must never block signing out.
    try {
      const presenceToken = localStorage.getItem('auth_token')
      if (presenceToken) {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'
        fetch(`${apiUrl}/api/messages/presence/leave`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${presenceToken}`,
            'Content-Type': 'application/json'
          },
          body: '{}',
          keepalive: true
        }).catch(() => {})
      }
    } catch {
      // never let presence cleanup interfere with logout
    }

    // Clear access level cache to prevent stale data for next user
    clearAccessLevelCache()
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('needsWelcome')
    localStorage.removeItem('token_refresh_lock')
    setUser(null)
  }, [])

  // Expose logout handler for global 401 interceptor
  useEffect(() => {
    window.__authLogout = performLogout

    return () => {
      delete window.__authLogout
    }
  }, [performLogout])

  // Check if user has existing JWT session (user or applicant) - VALIDATES WITH BACKEND
  async function checkSession() {
    try {
      setIsLoading(true)
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      // ======================================================================
      // CHECK 1: Applicant session (email verified but no profile)
      // ======================================================================
      const applicantToken = localStorage.getItem('applicant_token')
      const applicantData = localStorage.getItem('applicant_data')

      if (applicantToken && applicantData) {
        try {
          const parsedApplicant = JSON.parse(applicantData) as ApplicantUser
          setApplicant(parsedApplicant)
        } catch (parseErr) {
          console.error('Failed to parse applicant data:', parseErr)
          localStorage.removeItem('applicant_token')
          localStorage.removeItem('applicant_data')
        }
      }

      // ======================================================================
      // CHECK 2: User session (profile exists) - VALIDATE WITH BACKEND
      // ======================================================================
      const token = localStorage.getItem('auth_token')
      const userData = localStorage.getItem('user_data')

      if (token && userData) {
        // Validate token with backend (catches expired tokens)
        try {
          const validateResponse = await fetch(`${API_URL}/api/auth/validate-token`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (validateResponse.ok) {
            const validateData = await validateResponse.json()
            // Use fresh user data from backend
            setUser(validateData.user)
            localStorage.setItem('user_data', JSON.stringify(validateData.user))

            if (validateData.needsWelcome) {
              localStorage.setItem('needsWelcome', 'true')
            } else {
              localStorage.removeItem('needsWelcome')
            }
          } else {
            const errorData = await validateResponse.json().catch(() => ({}))
            console.warn('[AuthContext] Token validation failed:', errorData)

            // If token expired, try to refresh
            if (errorData.code === 'TOKEN_EXPIRED') {
              const refreshResponse = await fetch(`${API_URL}/api/auth/refresh-token`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })

              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json()
                localStorage.setItem('auth_token', refreshData.token)
                localStorage.setItem('user_data', JSON.stringify(refreshData.user))
                setUser(refreshData.user)
              } else {
                // Refresh failed - clear session and logout
                console.warn('[AuthContext] Token refresh failed, clearing session')
                performLogout()
              }
            } else {
              // Other validation errors - clear session
              console.warn('[AuthContext] Clearing invalid session')
              performLogout()
            }
          }
        } catch (networkErr) {
          console.warn('[AuthContext] Network error during token validation, using cached data:', networkErr)
          // On network error, trust cached data (offline support)
          const parsedUser = JSON.parse(userData)
          setUser(parsedUser)
        }
      }
    } catch (err) {
      console.error('[AuthContext] Session check error:', err)
      // Clear invalid data
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      localStorage.removeItem('applicant_token')
      localStorage.removeItem('applicant_data')
      localStorage.removeItem('needsWelcome')
      setError(err instanceof Error ? err.message : 'Failed to check session')
    } finally {
      setIsLoading(false)
    }
  }

  // Request OTP code via email (calls YOUR backend with Resend)
  // Returns isApplicant flag if this email is not registered (applicant flow)
  async function requestOTP(email: string): Promise<{ isApplicant?: boolean }> {
    try {
      setIsLoading(true)
      setError(null)

      // Call YOUR backend API to generate and send OTP via Resend
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to send verification code')
      }

      // Return isApplicant flag for frontend handling
      return { isApplicant: data.isApplicant || false }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send code'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }

  // Verify OTP code and login - PROPER JWT AUTHENTICATION
  // Handles both user login (profile exists) and applicant login (email verified only)
  async function login(email: string, code: string): Promise<{ needsWelcome?: boolean; isApplicant?: boolean }> {
    try {
      setIsLoading(true)
      setError(null)

      // Call backend API to verify OTP
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      })

      const data = await response.json()

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Invalid verification code')
      }

      // ======================================================================
      // APPLICANT FLOW: Email verified but no profile
      // ======================================================================
      if (data.isApplicant && data.applicant) {
        // Store applicant token and data
        localStorage.setItem('applicant_token', data.token)
        localStorage.setItem('applicant_data', JSON.stringify(data.applicant))
        setApplicant(data.applicant)

        return { isApplicant: true }
      }

      // ======================================================================
      // NORMAL USER FLOW: Profile exists
      // ======================================================================

      // INDUSTRY STANDARD: Store JWT token in localStorage
      if (data.token) {
        localStorage.setItem('auth_token', data.token)
      }

      // Store user data
      if (data.user) {
        setUser(data.user)
        localStorage.setItem('user_data', JSON.stringify(data.user))
      }

      // Store needsWelcome in localStorage so WelcomeScreen can check it
      if (data.needsWelcome) {
        localStorage.setItem('needsWelcome', 'true')
      }

      // Store invite info if present (for subscription modal after onboarding)
      if (data.hasInvite && data.inviteToken) {
        localStorage.setItem('pending_invite_token', data.inviteToken)
        localStorage.setItem('has_pending_subscription', 'true')
      }

      // Store verified phone from application (skip OTP verification in profile setup)
      if (data.applicationPhone?.phone_verified && data.applicationPhone?.phone_number) {
        localStorage.setItem('application_phone', data.applicationPhone.phone_number)
      }

      return {
        needsWelcome: data.needsWelcome || false
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to verify code'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }

  // Login with Google ID token - same pathways as OTP verification
  async function loginWithGoogle(idToken: string): Promise<{ needsWelcome?: boolean; isApplicant?: boolean }> {
    try {
      setIsLoading(true)
      setError(null)

      // Call backend API to verify Google token
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }),
      })

      const data = await response.json()

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Google Sign-In failed')
      }

      // ======================================================================
      // APPLICANT FLOW: Email verified but no profile
      // ======================================================================
      if (data.isApplicant && data.applicant) {
        localStorage.setItem('applicant_token', data.token)
        localStorage.setItem('applicant_data', JSON.stringify(data.applicant))
        setApplicant(data.applicant)

        return { isApplicant: true }
      }

      // ======================================================================
      // NORMAL USER FLOW: Profile exists
      // ======================================================================

      if (data.token) {
        localStorage.setItem('auth_token', data.token)
      }

      if (data.user) {
        setUser(data.user)
        localStorage.setItem('user_data', JSON.stringify(data.user))
      }

      if (data.needsWelcome) {
        localStorage.setItem('needsWelcome', 'true')
      }

      if (data.hasInvite && data.inviteToken) {
        localStorage.setItem('pending_invite_token', data.inviteToken)
        localStorage.setItem('has_pending_subscription', 'true')
      }

      if (data.applicationPhone?.phone_verified && data.applicationPhone?.phone_number) {
        localStorage.setItem('application_phone', data.applicationPhone.phone_number)
      }

      return {
        needsWelcome: data.needsWelcome || false
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google Sign-In failed'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }

  // Logout user - Clear JWT from localStorage
  async function logout() {
    try {
      setIsLoading(true)

      // Clear access level cache to prevent stale data for next user
      clearAccessLevelCache()

      // Clear JWT token and user data
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      setUser(null)
    } catch (err) {
      console.error('Logout error:', err)
      setError(err instanceof Error ? err.message : 'Failed to logout')
    } finally {
      setIsLoading(false)
    }
  }

  // Clear error message
  function clearError() {
    setError(null)
  }

  // Logout applicant - Clear applicant token from localStorage
  function logoutApplicant() {
    localStorage.removeItem('applicant_token')
    localStorage.removeItem('applicant_data')
    setApplicant(null)
  }

  const value: AuthContextType = {
    user,
    applicant,
    isLoading,
    isAuthenticated: !!user,
    isApplicant: !!applicant,
    login,
    loginWithGoogle,
    logout,
    logoutApplicant,
    requestOTP,
    error,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Hook to use auth context - use this in components
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
